import { InputProps } from '@chakra-ui/react';
import {
  CustomFieldPermissionsEnum,
  CustomFieldTypeEnum,
  ICustomFieldRecordSchema,
  ICustomFieldResSchema,
} from '@monetize/types/app';
import { useMemo, useRef, useState } from 'react';
import { Control, Controller, useController } from 'react-hook-form';
import { getResolvedPermissions } from '../../utils/customFields';
import { arrayToObject, orderObjectsBy } from '../../utils/misc';
import {
  MCheckbox,
  MCustomNumberInput,
  MFlex,
  MFormField,
  MInput,
  MText,
} from '../Monetize';
import { DatePicker } from '../Monetize/DatePicker/DatePicker';
import { MainSearchInputV2 } from '../Monetize/MCustomSelect/components/MainSearchInputV2';
import { MCustomMultiSelect } from '../Monetize/MCustomSelect/MCustomMultiSelect';
import { MCustomSelect } from '../Monetize/MCustomSelect/MCustomSelect';

interface CustomFormDataFormFieldProps {
  customField: ICustomFieldResSchema;
  control: Control<ICustomFieldRecordSchema>;
  onSave: (field: string, value: any) => void;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  variant?: InputProps['variant'];
  isHorizontal?: boolean;
}

export const CustomFormDataFormField = ({
  customField,
  control,
  onSave,
  isDisabled,
  isReadOnly,
  variant,
  isHorizontal,
}: CustomFormDataFormFieldProps) => {
  const { key, displayLabel, tooltip, type, values } = customField;

  const canEdit = getResolvedPermissions(customField).includes(
    CustomFieldPermissionsEnum.WRITE,
  );

  const {
    field,
    formState: { errors },
  } = useController({ control, name: key });

  // this is used to filter available options - but don't want options to get removed if user changes value
  // e.x. if disable value is selected, it should show up in the list of options
  const [initialFieldValue] = useState(field.value);

  const { dropdownValues, dropdownsByValue } = useMemo(() => {
    return {
      dropdownValues:
        type !== CustomFieldTypeEnum.DROPDOWN &&
        type !== CustomFieldTypeEnum.MULTI_SELECT
          ? []
          : orderObjectsBy(values || [], ['order']).filter(
              ({ enabled, value }) => {
                // For multi-select dropdown, initialFieldValue is an array
                if (Array.isArray(initialFieldValue)) {
                  return enabled || initialFieldValue.includes(value);
                }
                // For dropdown, initialFieldValue is a string
                return enabled || value === initialFieldValue;
              },
            ),
      // Allows accessing the label based on the value
      dropdownsByValue: arrayToObject(values || [], 'value'),
    };
  }, [initialFieldValue, type, values]);

  const fieldProps = {
    isDisabled: isDisabled || isReadOnly || !canEdit,
    isReadOnly: isReadOnly || !canEdit,
    variant: isReadOnly ? ('readonly' as const) : variant,
  };

  // Tracks the value when a blur-saving field gains focus so we can skip the
  // onSave when the user tabs out without changing anything. A ref because it
  // is only a comparison baseline and should not trigger re-renders.
  const focusedValueRef = useRef<any>(undefined);

  // Saves only if the value changed since focus. Strings are trimmed before
  // comparison so whitespace-only edits don't trigger a save, and empty/blank
  // strings are treated the same as null/undefined. NUMBER values are compared
  // numerically because they load as numbers but MCustomNumberInput emits
  // strings, so 5 vs '5' would otherwise count as a change.
  const saveIfChanged = (currentValue: any) => {
    const normalize = (val: any) => {
      const trimmed = typeof val === 'string' ? val.trim() : val;
      if (trimmed == null || trimmed === '') {
        return null;
      }
      return type === CustomFieldTypeEnum.NUMBER ? Number(trimmed) : trimmed;
    };
    if (normalize(focusedValueRef.current) !== normalize(currentValue)) {
      onSave(key, currentValue);
    }
  };

  return (
    <MFormField
      error={errors[key]}
      label={type !== CustomFieldTypeEnum.CHECKBOX ? displayLabel : ''}
      tooltip={tooltip || ''}
      isHorizontal={isHorizontal}
      // A label is often the raw field key, and `annual_price_adjustment` has
      // no break opportunity — without this it runs straight over the field in
      // the next column. Checkbox labels already break; this matches them.
      labelProps={{ wordBreak: 'break-word' }}
    >
      <Controller
        name={key}
        control={control}
        render={({ field: { value, onBlur, onChange, ...rest } }) => {
          switch (type) {
            case CustomFieldTypeEnum.CHECKBOX: {
              return (
                <MCheckbox
                  isChecked={!!value}
                  isDisabled={fieldProps.isDisabled}
                  isReadOnly={fieldProps.isReadOnly}
                  onChange={(e) => {
                    onChange(e);
                    onSave(key, e.target.checked);
                  }}
                  {...rest}
                  wordBreak="break-word"
                  mb="1.5"
                >
                  {displayLabel}
                </MCheckbox>
              );
            }
            case CustomFieldTypeEnum.NUMBER: {
              if (fieldProps.isReadOnly && !value) {
                return (
                  <MText fontStyle="italic" mt="2">
                    Blank
                  </MText>
                );
              }
              if (!canEdit) {
                return <MText mt="2">{value}</MText>;
              }
              return (
                <MCustomNumberInput
                  placeholder={displayLabel}
                  inputMode="decimal"
                  value={value as number}
                  onFocus={() => {
                    focusedValueRef.current = value;
                  }}
                  onBlur={() => {
                    saveIfChanged(value);
                  }}
                  onChange={(val) => onChange(val)}
                  {...rest}
                  {...fieldProps}
                />
              );
            }
            case CustomFieldTypeEnum.DATE: {
              if (fieldProps.isReadOnly && !value) {
                return (
                  <MText fontStyle="italic" mt="2">
                    Blank
                  </MText>
                );
              }
              return (
                <DatePicker
                  placeholder={displayLabel}
                  value={value as string}
                  clearable
                  onBlur={onBlur}
                  onChange={(e) => {
                    onChange(e);
                    onSave(key, e);
                  }}
                  {...rest}
                  {...fieldProps}
                />
              );
            }
            case CustomFieldTypeEnum.DROPDOWN: {
              if (fieldProps.isReadOnly && !value) {
                return (
                  <MText fontStyle="italic" mt="2">
                    Blank
                  </MText>
                );
              }
              if (!canEdit) {
                return (
                  <MText mt="2">
                    {dropdownsByValue[String(value)]?.label || value}
                  </MText>
                );
              }
              return (
                <MCustomSelect
                  value={value || ''}
                  clearable
                  useMainInputAsSearchInput
                  MainInputComponent={MainSearchInputV2}
                  itemTitle="label"
                  itemValue="value"
                  items={dropdownValues}
                  placeholder={`Select...`}
                  onChange={(newValue) => {
                    onChange(newValue);
                    onSave(key, newValue);
                  }}
                  {...rest}
                  {...fieldProps}
                />
              );
            }
            case CustomFieldTypeEnum.MULTI_SELECT: {
              const multiSelectValue = Array.isArray(value)
                ? value
                : value
                  ? [value]
                  : [];

              if (fieldProps.isReadOnly && multiSelectValue.length === 0) {
                return (
                  <MText fontStyle="italic" mt="2">
                    Blank
                  </MText>
                );
              }
              if (fieldProps.isReadOnly && multiSelectValue.length !== 0) {
                return (
                  <MFlex
                    mt={2}
                    flexWrap="wrap"
                    columnGap={1}
                    rowGap={1}
                    alignItems="center"
                  >
                    {multiSelectValue.map((val, idx) => (
                      <MText key={idx}>
                        {dropdownsByValue[String(val)]?.label || val}
                        {idx < multiSelectValue.length - 1 ? ',' : ''}
                      </MText>
                    ))}
                  </MFlex>
                );
              }
              if (!canEdit) {
                return (
                  <MFlex
                    mt="2"
                    flexWrap="wrap"
                    columnGap={1}
                    rowGap={1}
                    alignItems="center"
                  >
                    {multiSelectValue.map((val, idx) => (
                      <MText key={idx} as="span">
                        {dropdownsByValue[String(val)]?.label || val}
                        {idx < multiSelectValue.length - 1 ? ',' : ''}
                      </MText>
                    ))}
                  </MFlex>
                );
              }
              return (
                <MCustomMultiSelect
                  clearable
                  value={multiSelectValue}
                  itemTitle="label"
                  itemValue="value"
                  items={dropdownValues}
                  multiple
                  placeholder={`Select...`}
                  closeButtonText="Apply"
                  onChange={(selectedValues) => {
                    onChange(selectedValues);
                    onSave(key, selectedValues);
                  }}
                  onClear={() => {
                    onChange([]);
                    onSave(key, []);
                  }}
                  {...rest}
                  {...fieldProps}
                />
              );
            }
            case CustomFieldTypeEnum.SINGLE_LINE_TEXT:
            default: {
              if (fieldProps.isReadOnly && !value) {
                return (
                  <MText fontStyle="italic" mt="2">
                    Blank
                  </MText>
                );
              }
              if (!canEdit) {
                return <MText mt="2">{value}</MText>;
              }
              return (
                <MInput
                  placeholder={displayLabel}
                  value={(value || '') as string}
                  onFocus={() => {
                    focusedValueRef.current = value;
                  }}
                  onBlur={() => {
                    saveIfChanged(value);
                  }}
                  onChange={onChange}
                  {...rest}
                  {...fieldProps}
                />
              );
            }
          }
        }}
      />
    </MFormField>
  );
};
